import { Pagination } from '@/shared/components/Pagination';
import { formatDate } from '@/shared/utils/date';
import { cilCheckCircle, cilInfo, cilX } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import {
  CAlert,
  CButton,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CTooltip,
} from '@coreui/react';
import React from 'react';
import type { CatSubscription } from '../../types';
import { formatEstimatedDobAndAge } from '../../utils/catSubscriptionFormatting';
import { getDislikesDisplay, renderValue } from './utils';

interface CatSubscriptionsTableProps {
  catSubscriptions: CatSubscription[];
  loading: boolean;
  error: string | null;
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onView?: (catSubscription: CatSubscription) => void;
  onEdit?: (catSubscription: CatSubscription) => void;
}

export const CatSubscriptionsTable: React.FC<CatSubscriptionsTableProps> = ({
  catSubscriptions,
  loading,
  error,
  page,
  perPage,
  total,
  totalPages,
  onPageChange,
  onPerPageChange,
  onView,
  onEdit,
}) => {
  if (loading) {
    return <div className="text-center">Loading...</div>;
  }

  return (
    <>
      {error && <CAlert color="danger">{error}</CAlert>}
      {!error && (
        <>
          {catSubscriptions.length > 0 ? (
            <>
              <div className="d-flex align-items-center gap-3 mb-3">
                <span>
                  <span style={{ cursor: 'default' }}>😢</span> Cat has passed away
                </span>
                <span>
                  <span style={{ cursor: 'default' }}>🤧</span> Cat has allergies
                </span>
                <span className="d-inline-flex align-items-center gap-2">
                  <span className="d-inline-flex align-items-center" style={{ cursor: 'default' }}>
                    <CIcon icon={cilInfo} className="text-info" />
                  </span>
                  Note is added
                </span>
              </div>
              <CTable hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Name</CTableHeaderCell>
                    <CTableHeaderCell>Notes</CTableHeaderCell>
                    <CTableHeaderCell>Added</CTableHeaderCell>
                    <CTableHeaderCell>Estimated DOB and age</CTableHeaderCell>
                    <CTableHeaderCell>Tins/day</CTableHeaderCell>
                    <CTableHeaderCell>Food Type</CTableHeaderCell>
                    <CTableHeaderCell>Dislikes</CTableHeaderCell>
                    <CTableHeaderCell>Actions</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {catSubscriptions.map((catSubscription: CatSubscription) => (
                    <CTableRow key={catSubscription.id}>
                      <CTableDataCell>
                        <span className="d-inline-flex align-items-center gap-2">
                          {catSubscription.name}
                          <CTooltip
                            content={
                              catSubscription.hasActiveSubscription
                                ? 'Cat has subscription'
                                : 'Cat subscription was removed'
                            }
                          >
                            <span
                              className="d-inline-flex align-items-center"
                              style={{ cursor: 'default' }}
                            >
                              <CIcon
                                icon={catSubscription.hasActiveSubscription ? cilCheckCircle : cilX}
                                size="sm"
                                className={
                                  catSubscription.hasActiveSubscription
                                    ? 'text-success'
                                    : 'text-danger'
                                }
                              />
                            </span>
                          </CTooltip>
                        </span>
                      </CTableDataCell>
                      <CTableDataCell>
                        {catSubscription.passedAway ||
                        catSubscription.hasAllergies ||
                        catSubscription.notes ? (
                          <span className="d-inline-flex align-items-center gap-2">
                            {catSubscription.passedAway && (
                              <span className="d-inline-flex align-items-center gap-1">
                                <CTooltip content="Passed away">
                                  <span
                                    role="img"
                                    aria-label="Passed away"
                                    style={{ cursor: 'default' }}
                                  >
                                    😢
                                  </span>
                                </CTooltip>
                                <span>Passed away</span>
                              </span>
                            )}
                            {catSubscription.hasAllergies && (
                              <span className="d-inline-flex align-items-center gap-1">
                                <CTooltip content="Has allergies">
                                  <span
                                    role="img"
                                    aria-label="Has allergies"
                                    style={{ cursor: 'default' }}
                                  >
                                    🤧
                                  </span>
                                </CTooltip>
                                <span>Has allergies</span>
                              </span>
                            )}
                            {catSubscription.notes && (
                              <span className="d-inline-flex align-items-center gap-1">
                                <CTooltip content={catSubscription.notes}>
                                  <span
                                    className="d-inline-flex align-items-center"
                                    style={{ cursor: 'default' }}
                                  >
                                    <CIcon icon={cilInfo} className="text-info" />
                                  </span>
                                </CTooltip>
                                <span>Notes</span>
                              </span>
                            )}
                          </span>
                        ) : null}
                      </CTableDataCell>
                      <CTableDataCell>
                        {catSubscription.added || formatDate(catSubscription.createdAt)}
                      </CTableDataCell>
                      <CTableDataCell>
                        {renderValue(formatEstimatedDobAndAge(catSubscription))}
                      </CTableDataCell>
                      <CTableDataCell>{renderValue(catSubscription.tinsCount)}</CTableDataCell>
                      <CTableDataCell>{renderValue(catSubscription.mealType)}</CTableDataCell>
                      <CTableDataCell>{getDislikesDisplay(catSubscription)}</CTableDataCell>
                      <CTableDataCell>
                        <span className="d-inline-flex align-items-center gap-3">
                          {onEdit && (
                            <CButton
                              color="primary"
                              size="sm"
                              onClick={() => onEdit(catSubscription)}
                            >
                              Edit
                            </CButton>
                          )}
                          {onView && (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                onView(catSubscription);
                              }}
                              style={{ color: '#0066cc', textDecoration: 'none' }}
                            >
                              Raw Data
                            </a>
                          )}
                        </span>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
              <div className="d-flex justify-content-center mt-3">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  perPage={perPage}
                  total={total}
                  onPageChange={onPageChange}
                  onPerPageChange={onPerPageChange}
                />
              </div>
            </>
          ) : (
            <CAlert color="info">No cat subscriptions found for this customer.</CAlert>
          )}
        </>
      )}
    </>
  );
};
